import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { toast } from 'sonner';
import {
  Users,
  Search,
  RefreshCw,
  Shield,
  ShieldAlert,
  AlertTriangle,
  UserPlus,
  Pencil,
  Save,
  Flag,
  Lock,
  FlaskConical,
  Layers
} from 'lucide-react';
import { usersAdminApi } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import PageHeader from '../../components/PageHeader';
import { TableScroll, StatGrid } from '../../components/ResponsiveTable';
import usePageTitle from '../../hooks/usePageTitle';
import { cn } from '@/lib/utils';

/*
 * /admin/users - the React face of the legacy /userDataManagement/ page (U5, 2026-08-23).
 *
 * The server delegates every write to the SAME UserService the Thymeleaf page calls, so what you
 * can do here is exactly what that page can do, made explicit:
 *   - list / add / edit accounts (name, password, active, slots, devices);
 *   - the role ("Motto" on the old form) - super admin only;
 *   - the user-level switches that only existed as URL hacks before
 *     (/userDataManagement/edit/{email}/0/alphatester/enable): cohorts, the plan ladder, features.
 * Three legacy facts this page states rather than hides:
 *   - "Active" gates the worker/session API for the account, NOT login;
 *   - slots and devices can only GROW (legacy appends rows; it never removes);
 *   - a role change reaches the person at their next login; the new endpoints re-check the
 *     database on every call, the old pages trust the session until logout.
 * Design + review record: NecroMancerStack/plans/u5-user-admin-design.md.
 *
 * Aurora pass (2026-08-23): PageHeader, glass cards, semantic tokens, a card list for the users
 * table under md, and dialogs that fit a 375px phone. Behaviour, copy and testids are unchanged.
 */

const ROLE_LABEL = { ROLE_ADMIN: 'Admin', ROLE_USER: 'User' };

// Switch groups. Keys are the server's allow-list (legacy spelling); the ladder order matters for
// the hint - enabling a rung enables every rung below it, disabling one clears everything above.
const COHORT_TOGGLES = [
  { key: 'alphatester', label: 'Alpha tester', read: (u) => u.alpha_tester, hint: 'Gets every feature flag whose rollout % is above 0' },
  { key: 'betatester', label: 'Beta tester', read: (u) => u.beta_tester, hint: 'Same as alpha - both cohorts enter together' },
  { key: 'privatedashboard', label: 'Private dashboard', read: (u) => u.private_dashboard, hint: 'showPrivateDashboard on the legacy dashboard' },
];
const PLAN_TOGGLES = [
  { key: 'dabblerplan', label: 'Dabbler', read: (u) => u.plan?.dabbler },
  { key: 'hobbyistplan', label: 'Hobbyist', read: (u) => u.plan?.hobbyist },
  { key: 'startupplan', label: 'Startup', read: (u) => u.plan?.startup },
  { key: 'enterpriseplan', label: 'Enterprise', read: (u) => u.plan?.enterprise, hint: 'Also turns on AI hyper-personalisation' },
  { key: 'unlimitedplan', label: 'Unlimited', read: (u) => u.plan?.unlimited },
  { key: 'floodingsettings', label: 'Flooding settings', read: (u) => u.plan?.flooding, hint: 'Top rung - enables every plan below it' },
  { key: 'enableaihyperp', label: 'AI hyper-personalisation', read: (u) => u.plan?.ai_hyper_p },
];
export const FEATURE_TOGGLES = [
  { key: 'enablesmmfeature', label: 'SMM feature', read: (u) => u.features?.smm },
  { key: 'enablesnspanel', label: 'SNS panel', read: (u) => u.features?.sns_panel },
  { key: 'enableperfectpanel', label: 'Perfect panel', read: (u) => u.features?.perfect_panel },
  { key: 'smmscrapingservice', label: 'SMM scraping service', read: (u) => u.features?.smm_scraping },
  // r16 TikTok review must-fix 4 (2026-10-03): who may set a slot's Platform (setting 15) to TikTok - User.enableTikTok, the server's
  // "enabletiktok" switch. Shown only when the server reports it (features.tiktok, an r16 server): an older server has no such switch.
  {
    key: 'enabletiktok', label: 'TikTok', read: (u) => u.features?.tiktok,
    hint: "May set a slot's Platform to TikTok (admins always may)",
    requires: (u) => !!u.features && Object.prototype.hasOwnProperty.call(u.features, 'tiktok'),
  },
];

/** The switches of a group this user's row can show: an entry with `requires` only when the server reported its field. */
export function visibleToggles(list, user) {
  return list.filter((t) => !t.requires || t.requires(user || {}));
}

/* Shared Aurora class strings. Tailwind needs every class spelled out, so these are full strings. */
const LABEL = 'text-xs font-medium uppercase tracking-wide text-muted-foreground';
const HINT = 'normal-case tracking-normal text-muted-foreground/70';
const FIELD = 'bg-secondary/60 border-border';
const SECTION = 'font-heading text-base font-semibold text-foreground flex items-center gap-2 mb-1';

function errMsg(err, fallback) {
  return err?.response?.data?.error || err?.message || fallback;
}

function planLabel(u) {
  if (u.plan?.flooding) return 'Flooding';
  if (u.plan?.unlimited) return 'Unlimited';
  if (u.plan?.enterprise) return 'Enterprise';
  if (u.plan?.startup) return 'Startup';
  if (u.plan?.hobbyist) return 'Hobbyist';
  if (u.plan?.dabbler) return 'Dabbler';
  return 'No plan';
}

/* ---------------------------------------------------------------- honest states */

function FlagOffCard({ message, isSuperAdmin }) {
  return (
    <Card className="rounded-2xl border-warning/40 bg-warning/10 shadow-none" data-testid="users-flag-off">
      <CardContent className="p-5 sm:p-6 flex items-start gap-3 text-sm text-foreground/90">
        <Flag className="h-5 w-5 text-warning shrink-0 mt-0.5" />
        <div className="min-w-0">
          <div className="font-semibold text-foreground">User management is switched off on this server.</div>
          <div className="text-muted-foreground mt-1">
            The server said: <span className="font-mono break-all">{message}</span>. Turn on{' '}
            <span className="font-mono">UIAPI_USER_ADMIN</span>{' '}
            {isSuperAdmin
              ? <>on the <Link className="underline text-foreground" to="/admin/feature-flags">Flags</Link> page (takes effect within 10 s).</>
              : <>- only the super admin can, on the Flags page.</>}
            {' '}Until then the legacy page at <span className="font-mono">/userDataManagement/</span> still works.
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function DeniedCard({ message }) {
  return (
    <Card className="rounded-2xl border-danger/40 bg-danger/10 shadow-none" data-testid="users-denied">
      <CardContent className="p-5 sm:p-6 flex items-start gap-3 text-sm text-foreground/90">
        <ShieldAlert className="h-5 w-5 text-danger shrink-0 mt-0.5" />
        <div className="min-w-0">
          <div className="font-semibold text-foreground">This account cannot manage users.</div>
          <div className="text-muted-foreground mt-1">The server said: <span className="font-mono break-all">{message}</span>.</div>
        </div>
      </CardContent>
    </Card>
  );
}

/* ---------------------------------------------------------------- add */

function AddUserDialog({ open, onOpenChange, superAdmin, roles, onCreated }) {
  const empty = { first_name: '', last_name: '', email: '', password: '', role: 'ROLE_USER', account_active: true, slots: 1, devices: 0 };
  const [form, setForm] = useState(empty);
  useEffect(() => { if (open) setForm(empty); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const create = useMutation({
    mutationFn: () => usersAdminApi.create({
      ...form,
      slots: Number(form.slots),
      devices: Number(form.devices),
      role: superAdmin ? form.role : undefined,
    }),
    onSuccess: (res) => {
      toast.success(`${res.data?.email} created with ${res.data?.slots} slot(s)`);
      onCreated();
      onOpenChange(false);
    },
    onError: (err) => toast.error(errMsg(err, 'Create failed')),
  });

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-2rem)] sm:w-full sm:max-w-xl max-h-[90vh] overflow-y-auto rounded-xl border-border bg-card">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-foreground"><UserPlus className="h-5 w-5 text-primary" />Add user</DialogTitle>
          <DialogDescription>
            Same as the legacy "Add New User" form. The server creates the slot and device rows immediately.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
          <div className="space-y-1.5">
            <Label className={LABEL}>First name</Label>
            <Input value={form.first_name} onChange={set('first_name')} className={FIELD} data-testid="add-first-name" />
          </div>
          <div className="space-y-1.5">
            <Label className={LABEL}>Last name</Label>
            <Input value={form.last_name} onChange={set('last_name')} className={FIELD} data-testid="add-last-name" />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label className={LABEL}>Email</Label>
            <Input type="email" value={form.email} onChange={set('email')} className={FIELD} data-testid="add-email" />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label className={LABEL}>Password</Label>
            <Input type="password" autoComplete="new-password" value={form.password} onChange={set('password')} className={FIELD} data-testid="add-password" />
          </div>
          <div className="space-y-1.5">
            <Label className={LABEL}>Slots</Label>
            <Input type="number" min={0} max={200} value={form.slots} onChange={set('slots')} className={cn(FIELD, 'tnum')} data-testid="add-slots" />
          </div>
          <div className="space-y-1.5">
            <Label className={LABEL}>Devices</Label>
            <Input type="number" min={0} max={200} value={form.devices} onChange={set('devices')} className={cn(FIELD, 'tnum')} data-testid="add-devices" />
          </div>
          {superAdmin && (
            <div className="space-y-1.5">
              <Label className={LABEL}>Role</Label>
              <Select value={form.role} onValueChange={set('role')}>
                <SelectTrigger className={FIELD} data-testid="add-role"><SelectValue /></SelectTrigger>
                <SelectContent className="border-border">
                  {roles.map((r) => <SelectItem key={r} value={r}>{ROLE_LABEL[r] || r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-1.5">
            <Label className={LABEL}>Automation active</Label>
            <div className="flex items-center gap-2 min-h-10">
              <Switch checked={form.account_active} onCheckedChange={set('account_active')} data-testid="add-active" />
              <span className="text-xs text-muted-foreground">Off = the worker API refuses this account. Login is unaffected.</span>
            </div>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="ghost" className="tap" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => create.mutate()} disabled={create.isPending} className="tap" data-testid="add-save">
            {create.isPending ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------------------------------------------------------- edit */

function ToggleRow({ user, t, disabled, onToggle, pending }) {
  const on = !!t.read(user);
  return (
    <div className="flex items-center justify-between gap-3 py-2 border-b border-border/60 last:border-0" data-testid={`toggle-${t.key}`}>
      <div className="min-w-0">
        <div className="text-sm text-foreground">{t.label}</div>
        {t.hint && <div className="text-xs text-muted-foreground">{t.hint}</div>}
      </div>
      <Switch checked={on} disabled={disabled || pending} onCheckedChange={(v) => onToggle(t.key, v)} />
    </div>
  );
}

function EditUserDialog({ user, open, onOpenChange, superAdmin, roles, onChanged }) {
  const [form, setForm] = useState(null);
  const [current, setCurrent] = useState(user);
  useEffect(() => {
    if (open && user) {
      setCurrent(user);
      setForm({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        password: '',
        role: user.is_admin ? 'ROLE_ADMIN' : 'ROLE_USER',
        account_active: !!user.account_active,
        slots: user.slots ?? 0,
        devices: user.devices ?? 0,
      });
    }
  }, [open, user]);

  const adminLocked = current?.is_admin && !superAdmin;      // server: 403 "super admin only"
  const roleLocked = !superAdmin || current?.protected;         // server: 409 on a protected row
  // The server can only derive the role for legacy when the row holds exactly one of the two
  // known names; otherwise it insists on an explicit one (legacy replaces the whole set from a
  // single name). So for any other shape the page always sends the role it shows - ROLE_ADMIN on
  // a protected row, which the server accepts as a no-op.
  const distinctRoles = [...new Set(current?.roles || [])];
  const needsExplicitRole = distinctRoles.length !== 1 || !roles.includes(distinctRoles[0]);

  const save = useMutation({
    mutationFn: () => {
      const body = {};
      if (form.first_name !== (current.first_name || '')) body.first_name = form.first_name;
      if (form.last_name !== (current.last_name || '')) body.last_name = form.last_name;
      if (form.password.trim()) body.password = form.password;
      // A row whose column is NULL must get an explicit value (the server refuses to coalesce
      // it, because legacy would store NULL and the worker API NPEs on it).
      if (current.account_active_is_null || form.account_active !== !!current.account_active) body.account_active = form.account_active;
      if (Number(form.slots) !== current.slots) body.slots = Number(form.slots);
      if (Number(form.devices) !== current.devices) body.devices = Number(form.devices);
      const currentRole = current.is_admin ? 'ROLE_ADMIN' : 'ROLE_USER';
      if (superAdmin && needsExplicitRole) body.role = current.protected ? 'ROLE_ADMIN' : form.role;
      else if (!roleLocked && form.role !== currentRole) body.role = form.role;
      if (Object.keys(body).length === 0) return Promise.resolve({ data: current, unchanged: true });
      return usersAdminApi.update(current.id, body);
    },
    onSuccess: (res) => {
      if (res.unchanged) { toast.info('Nothing changed'); return; }
      setCurrent(res.data);
      setForm((f) => ({ ...f, password: '' }));
      toast.success(`${res.data.email} saved`);
      onChanged();
    },
    onError: (err) => toast.error(errMsg(err, 'Save failed')),
  });

  const toggle = useMutation({
    mutationFn: ({ feature, enabled }) => usersAdminApi.toggle(current.id, feature, enabled),
    onSuccess: (res, vars) => {
      setCurrent(res.data);
      toast.success(`${vars.feature} ${vars.enabled ? 'on' : 'off'} for ${res.data.email}`);
      onChanged();
    },
    onError: (err) => toast.error(errMsg(err, 'Switch failed')),
  });

  if (!form || !current) return null;
  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });
  const onToggle = (feature, enabled) => toggle.mutate({ feature, enabled });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-2rem)] sm:w-full sm:max-w-2xl max-h-[90vh] overflow-y-auto rounded-xl border-border bg-card">
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-2 text-foreground pr-6">
            <Pencil className="h-5 w-5 text-primary shrink-0" />
            <span className="min-w-0 truncate">{current.email}</span>
            {current.is_admin && <Badge className="bg-warning/15 text-warning border-transparent">Admin</Badge>}
            {current.protected && <Badge variant="outline" className="border-border text-muted-foreground"><Lock className="h-3 w-3 mr-1" />protected</Badge>}
          </DialogTitle>
          <DialogDescription>
            Same rules as the legacy edit form. Email cannot be changed; slots and devices can only grow (disable a slot instead of removing it).
          </DialogDescription>
        </DialogHeader>

        {adminLocked && (
          <div className="flex items-start gap-2 p-3 rounded-xl bg-warning/10 border border-warning/20 text-xs text-warning">
            <Lock className="h-4 w-4 shrink-0" /> <span>This account holds ROLE_ADMIN - only the super admin (the owner account) can edit it. Legacy enforces the same rule.</span>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
          <div className="space-y-1.5">
            <Label className={LABEL}>First name</Label>
            <Input value={form.first_name} onChange={set('first_name')} disabled={adminLocked} className={FIELD} data-testid="edit-first-name" />
          </div>
          <div className="space-y-1.5">
            <Label className={LABEL}>Last name</Label>
            <Input value={form.last_name} onChange={set('last_name')} disabled={adminLocked} className={FIELD} data-testid="edit-last-name" />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label className={LABEL}>New password <span className={HINT}>(leave blank to keep)</span></Label>
            <Input type="password" autoComplete="new-password" value={form.password} onChange={set('password')} disabled={adminLocked} className={FIELD} data-testid="edit-password" />
          </div>
          <div className="space-y-1.5">
            <Label className={LABEL}>Slots <span className={cn(HINT, 'tnum')}>(now {current.slots})</span></Label>
            <Input type="number" min={current.slots ?? 0} max={200} value={form.slots} onChange={set('slots')} disabled={adminLocked} className={cn(FIELD, 'tnum')} data-testid="edit-slots" />
          </div>
          <div className="space-y-1.5">
            <Label className={LABEL}>Devices <span className={cn(HINT, 'tnum')}>(now {current.devices})</span></Label>
            <Input type="number" min={current.devices ?? 0} max={200} value={form.devices} onChange={set('devices')} disabled={adminLocked} className={cn(FIELD, 'tnum')} data-testid="edit-devices" />
          </div>
          {superAdmin && (
            <div className="space-y-1.5">
              <Label className={LABEL}>Role</Label>
              <Select value={form.role} onValueChange={set('role')} disabled={roleLocked}>
                <SelectTrigger className={FIELD} data-testid="edit-role"><SelectValue /></SelectTrigger>
                <SelectContent className="border-border">
                  {roles.map((r) => <SelectItem key={r} value={r}>{ROLE_LABEL[r] || r}</SelectItem>)}
                </SelectContent>
              </Select>
              {needsExplicitRole && (
                <p className="text-xs text-warning" data-testid="edit-role-explicit">
                  This account holds roles [{(current.roles || []).join(', ') || 'none'}]. Saving writes the single role shown
                  {current.protected ? ' (Admin)' : ''} and drops the others - that is how the legacy edit works.
                </p>
              )}
              {current.protected
                ? <p className="text-xs text-muted-foreground">Protected: the owner account, a listed super admin, or yourself. ROLE_ADMIN cannot be removed from it here.</p>
                : <p className="text-xs text-muted-foreground">Applies at the person's next login. Their current session keeps its role on the legacy pages until they log out.</p>}
            </div>
          )}
          <div className="space-y-1.5">
            <Label className={LABEL}>Automation active</Label>
            <div className="flex items-center gap-2 min-h-10">
              <Switch checked={form.account_active} disabled={adminLocked} onCheckedChange={set('account_active')} data-testid="edit-active" />
              <span className="text-xs text-muted-foreground">Off = worker API refused. Does not block login.</span>
            </div>
            {current.account_active_is_null && (
              <p className="text-xs text-warning" data-testid="edit-active-null">
                This row has NO value here (NULL) - the worker API fails for this account until one is saved. Save writes {form.account_active ? 'On' : 'Off'}.
              </p>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2 pb-4 border-b border-border">
          <Button variant="ghost" className="tap" onClick={() => onOpenChange(false)}>Close</Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending || adminLocked} className="tap" data-testid="edit-save">
            {save.isPending ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save
          </Button>
        </DialogFooter>

        <div className="space-y-4 pt-2">
          <div className="text-xs text-muted-foreground">
            Switches apply immediately, one at a time - they are the legacy
            <span className="font-mono break-all"> /userDataManagement/edit/&#123;email&#125;/0/&#123;feature&#125;/enable</span> URLs with a face.
          </div>
          <div>
            <div className={SECTION}><FlaskConical className="h-4 w-4 text-accent" />Cohorts</div>
            {COHORT_TOGGLES.map((t) => <ToggleRow key={t.key} t={t} user={current} disabled={adminLocked} pending={toggle.isPending} onToggle={onToggle} />)}
          </div>
          <div>
            <div className={SECTION}><Layers className="h-4 w-4 text-success" />Plan ladder</div>
            <p className="text-xs text-muted-foreground mb-1">Turning a rung on also turns on every rung below it; turning one off clears everything above it. That is the legacy logic, shown as it is.</p>
            {PLAN_TOGGLES.map((t) => <ToggleRow key={t.key} t={t} user={current} disabled={adminLocked} pending={toggle.isPending} onToggle={onToggle} />)}
          </div>
          <div>
            <div className={SECTION}><Shield className="h-4 w-4 text-primary" />Features</div>
            {visibleToggles(FEATURE_TOGGLES, current).map((t) => <ToggleRow key={t.key} t={t} user={current} disabled={adminLocked} pending={toggle.isPending} onToggle={onToggle} />)}
          </div>
          <div className="text-xs text-muted-foreground tnum">
            Scraping credits: {current.scraping_credits ?? 0} · post-scraping credits: {current.post_scraping_credits ?? 0} (added through the legacy URL only).
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------------------------------------------------------- page */

/* The per-user chips, shared by the table row (md+) and the card (phones) so they never drift. */
function RoleChips({ user }) {
  return (
    <div className="flex flex-wrap items-center gap-1">
      {user.is_admin
        ? <Badge className="bg-warning/15 text-warning border-transparent">Admin</Badge>
        : <Badge variant="outline" className="border-border text-muted-foreground">User</Badge>}
      {user.protected && <Lock className="h-3.5 w-3.5 text-muted-foreground self-center" title="Protected: ROLE_ADMIN cannot be removed here" />}
      {user.roles?.filter((r) => r !== 'ROLE_ADMIN' && r !== 'ROLE_USER').map((r) => (
        <Badge key={r} variant="outline" className="border-danger/50 text-danger">{r}</Badge>
      ))}
    </div>
  );
}

function AutomationChip({ user }) {
  if (user.account_active_is_null) {
    return (
      <Badge className="bg-warning/15 text-warning border-transparent" title="The column is NULL: the worker API fails for this account. Open Edit and save once to set it.">
        No value
      </Badge>
    );
  }
  return (
    <Badge
      className={cn('border-transparent', user.account_active ? 'bg-success/15 text-success' : 'bg-muted text-muted-foreground')}
      title="Gates the worker API, not login"
    >
      {user.account_active ? 'Active' : 'Inactive'}
    </Badge>
  );
}

function CohortChips({ user }) {
  /* Cohorts decide flag rollouts: the moment a flag's rollout % is above 0, every alpha
     and beta tester has it ON. So this column is explicit - a dash means "not a tester". */
  return (
    <div className="flex flex-wrap gap-1">
      {user.alpha_tester && <Badge className="bg-accent/15 text-accent border-transparent" title="Alpha tester: gets every flag whose rollout % is above 0">Alpha tester</Badge>}
      {user.beta_tester && <Badge variant="outline" className="border-accent/40 text-accent" title="Beta tester: gets every flag whose rollout % is above 0">Beta tester</Badge>}
      {!user.alpha_tester && !user.beta_tester && <span className="text-muted-foreground/60" title="Not a tester: gets a flag only by rollout bucket or 'enabled globally'">—</span>}
    </div>
  );
}

function PlanChips({ user }) {
  return (
    <div className="flex flex-wrap gap-1">
      <Badge variant="outline" className="border-success/40 text-success">{planLabel(user)}</Badge>
      {user.features?.smm && <Badge className="bg-primary/15 text-primary border-transparent">SMM</Badge>}
    </div>
  );
}

function Avatar({ user }) {
  return (
    <div className="h-9 w-9 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center text-foreground font-semibold shrink-0">
      {(user.first_name || user.email || '?')[0]?.toUpperCase()}
    </div>
  );
}

function EditButton({ user, locked, onEdit, testId, className }) {
  return (
    <Button
      variant="ghost" size="sm"
      className={cn('tap', locked ? 'text-muted-foreground' : 'text-primary hover:text-primary/80', className)}
      onClick={() => onEdit(user)}
      title={locked ? 'Super admin only: this account holds ROLE_ADMIN' : 'Edit account and switches'}
      data-testid={testId}
    >
      {locked ? <Lock className="h-4 w-4" /> : <Pencil className="h-4 w-4" />}
      {locked ? 'View' : 'Edit'}
    </Button>
  );
}

function UserRow({ user, superAdmin, onEdit }) {
  const locked = user.is_admin && !superAdmin;
  return (
    <TableRow className="border-border hover:bg-secondary/60" data-testid={`user-row-${user.id}`}>
      <TableCell>
        <div className="flex items-center gap-3 min-w-0">
          <Avatar user={user} />
          <div className="min-w-0">
            <p className="font-medium text-foreground truncate">{user.first_name} {user.last_name}</p>
            <p className="text-xs text-muted-foreground truncate">{user.email} · #{user.id}</p>
          </div>
        </div>
      </TableCell>
      <TableCell><RoleChips user={user} /></TableCell>
      <TableCell><AutomationChip user={user} /></TableCell>
      <TableCell className="text-foreground/90 tnum whitespace-nowrap">{user.slots ?? '–'} / {user.devices ?? '–'}</TableCell>
      <TableCell data-testid={`cohort-${user.id}`}><CohortChips user={user} /></TableCell>
      <TableCell><PlanChips user={user} /></TableCell>
      <TableCell>
        <EditButton user={user} locked={locked} onEdit={onEdit} testId={`edit-user-${user.id}`} />
      </TableCell>
    </TableRow>
  );
}

/* The same row as a card, for phones (< md). Testids are suffixed so the hidden twin never
   collides with the table row's. */
function UserCard({ user, superAdmin, onEdit }) {
  const locked = user.is_admin && !superAdmin;
  return (
    <div className="aurora-card p-4 space-y-3" data-testid={`user-card-${user.id}`}>
      <div className="flex items-start gap-3">
        <Avatar user={user} />
        <div className="min-w-0 flex-1">
          <p className="font-medium text-foreground truncate">{user.first_name} {user.last_name}</p>
          <p className="text-xs text-muted-foreground truncate">{user.email} · #{user.id}</p>
        </div>
        <EditButton user={user} locked={locked} onEdit={onEdit} testId={`edit-user-card-${user.id}`} className="-mr-2 -mt-1 shrink-0" />
      </div>
      <dl className="grid grid-cols-1 gap-x-4 gap-y-2 text-sm xs:grid-cols-2">
        <div className="min-w-0">
          <dt className={LABEL}>Role</dt>
          <dd className="mt-1"><RoleChips user={user} /></dd>
        </div>
        <div className="min-w-0">
          <dt className={LABEL}>Automation</dt>
          <dd className="mt-1"><AutomationChip user={user} /></dd>
        </div>
        <div className="min-w-0">
          <dt className={LABEL}>Slots / devices</dt>
          <dd className="mt-1 text-foreground/90 tnum">{user.slots ?? '–'} / {user.devices ?? '–'}</dd>
        </div>
        <div className="min-w-0">
          <dt className={LABEL}>Cohort</dt>
          <dd className="mt-1"><CohortChips user={user} /></dd>
        </div>
        <div className="min-w-0 xs:col-span-2">
          <dt className={LABEL}>Plan</dt>
          <dd className="mt-1"><PlanChips user={user} /></dd>
        </div>
      </dl>
    </div>
  );
}

/* A stat tile in the Dashboard's Aurora shape: icon chip, big number, small label. Local rather
   than StatTile because the testers tile needs the violet accent tone, which StatTile lacks. */
const STAT_TONE = {
  primary: 'bg-primary/15 text-primary ring-primary/25',
  success: 'bg-success/15 text-success ring-success/25',
  warning: 'bg-warning/15 text-warning ring-warning/25',
  accent: 'bg-accent/15 text-accent ring-accent/25',
};
function StatCard({ label, value, icon: Icon, tone = 'primary', onClick, testId }) {
  const Comp = onClick ? 'button' : 'div';
  return (
    <Comp
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      data-testid={testId}
      className={cn(
        'aurora-card flex items-center justify-between gap-3 p-4 sm:p-5 text-left w-full min-w-0',
        onClick && 'aurora-card-link tap hover:border-accent/40'
      )}
    >
      <div className="min-w-0">
        <p className="font-heading text-2xl font-semibold leading-none tracking-tight tnum text-foreground">{value}</p>
        <p className="mt-2 text-xs font-medium uppercase tracking-wide text-muted-foreground truncate">{label}</p>
      </div>
      <span className={cn('inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full ring-1', STAT_TONE[tone] || STAT_TONE.primary)}>
        <Icon className="h-5 w-5" />
      </span>
    </Comp>
  );
}

function UsersSkeleton() {
  return (
    <div className="space-y-2" aria-busy="true" aria-label="Loading users" data-testid="users-loading">
      {[0, 1, 2, 3, 4].map((i) => (
        <div key={i} className="flex items-center gap-3">
          <div className="h-9 w-9 shrink-0 rounded-full animate-pulse bg-secondary/60" />
          <div className="flex-1 space-y-2">
            <div className="h-3 w-1/3 animate-pulse rounded-xl bg-secondary/60" />
            <div className="h-3 w-1/2 animate-pulse rounded-xl bg-secondary/60" />
          </div>
          <div className="hidden sm:block h-5 w-16 animate-pulse rounded-xl bg-secondary/60" />
          <div className="hidden md:block h-5 w-20 animate-pulse rounded-xl bg-secondary/60" />
        </div>
      ))}
    </div>
  );
}

export default function UserManagementPage() {
  usePageTitle('Users');
  const queryClient = useQueryClient();
  const { user: me } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [cohort, setCohort] = useState('all'); // all | alpha | beta | none
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['adminUsers'],
    queryFn: () => usersAdminApi.list().then((res) => res.data),
    retry: (count, err) => err?.response?.status !== 403 && err?.response?.status !== 401 && count < 2,
    placeholderData: (prev) => prev,
  });
  const onChanged = () => queryClient.invalidateQueries({ queryKey: ['adminUsers'] });

  const status = error?.response?.status;
  const message = error?.response?.data?.error || error?.message;
  if (status === 403 && /feature flag/i.test(message || '')) {
    return <FlagOffCard message={message} isSuperAdmin={!!me?.isSuperAdmin} />;
  }
  if (status === 403) {
    return <DeniedCard message={message} />;
  }
  if (error) {
    return (
      <div className="aurora-card p-8 text-center sm:p-10" role="alert" data-testid="users-error">
        <span className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-full bg-danger/10 text-danger ring-1 ring-danger/20">
          <AlertTriangle className="h-6 w-6" />
        </span>
        <p className="mt-4 font-heading text-base font-semibold text-foreground">Failed to load users</p>
        <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground text-pretty break-words">{message}</p>
        <Button variant="outline" onClick={() => refetch()} className="tap mt-4 rounded-full">
          <RefreshCw className="h-4 w-4" /> Retry
        </Button>
      </div>
    );
  }

  const all = data?.users || [];
  const superAdmin = !!data?.super_admin;
  const roles = data?.roles || ['ROLE_USER', 'ROLE_ADMIN'];
  const q = searchTerm.trim().toLowerCase();
  const alphaCount = all.filter((u) => u.alpha_tester).length;
  const betaCount = all.filter((u) => u.beta_tester).length;
  const inCohort = (u) => cohort === 'all'
    || (cohort === 'alpha' && u.alpha_tester)
    || (cohort === 'beta' && u.beta_tester)
    || (cohort === 'none' && !u.alpha_tester && !u.beta_tester);
  const users = all.filter(inCohort).filter((u) => !q
    || u.email?.toLowerCase().includes(q)
    || `${u.first_name || ''} ${u.last_name || ''}`.toLowerCase().includes(q)
    || String(u.id) === q);

  return (
    <div className="space-y-6" data-testid="user-management-page">
      <PageHeader
        icon={Shield}
        title="User Management"
        description={<>Accounts, roles, slots and the per-user switches - the <span className="font-mono">/userDataManagement/</span> page, wired here.</>}
        actions={(
          <>
            <Button onClick={() => refetch()} variant="outline" className="tap border-border" data-testid="users-refresh">
              <RefreshCw className={cn('h-4 w-4', isFetching && 'animate-spin text-primary')} />
              Refresh
            </Button>
            <Button onClick={() => setAdding(true)} className="tap shadow-glow" data-testid="users-add">
              <UserPlus className="h-4 w-4" />
              Add user
            </Button>
          </>
        )}
      />

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        <Input
          placeholder="Search by email, name or id..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 bg-secondary/60 border-border placeholder:text-muted-foreground/70"
          aria-label="Search users"
          data-testid="users-search"
        />
      </div>

      {/* Cohort filter. These are the accounts a flag reaches first: rollout % > 0 = every
          alpha + beta tester ON, then the sticky bucket for everyone else. */}
      <div className="flex flex-wrap items-center gap-2 text-sm" data-testid="cohort-filter">
        <FlaskConical className="h-4 w-4 text-accent" />
        <span className="text-muted-foreground mr-1">Cohort:</span>
        {[
          { key: 'all', label: `All (${all.length})` },
          { key: 'alpha', label: `Alpha testers (${alphaCount})` },
          { key: 'beta', label: `Beta testers (${betaCount})` },
          { key: 'none', label: `Not testers (${all.length - all.filter((u) => u.alpha_tester || u.beta_tester).length})` },
        ].map((c) => (
          <Button
            key={c.key}
            size="sm"
            variant={cohort === c.key ? 'default' : 'outline'}
            className={cn('tnum', cohort === c.key ? 'bg-accent text-accent-foreground hover:bg-accent/90' : 'border-border text-foreground/90')}
            onClick={() => setCohort(c.key)}
            data-testid={`cohort-${c.key}`}
          >
            {c.label}
          </Button>
        ))}
        <span className="text-xs text-muted-foreground w-full sm:w-auto sm:ml-2">Testers get every flag whose rollout % is above 0. Change a cohort in Edit → Switches.</span>
      </div>

      <StatGrid cols={4}>
        <StatCard label="Total accounts" value={all.length} icon={Users} tone="primary" testId="stat-total" />
        <StatCard label="Automation active" value={all.filter((u) => u.account_active).length} icon={Shield} tone="success" testId="stat-active" />
        <StatCard label="Admins" value={all.filter((u) => u.is_admin).length} icon={Lock} tone="warning" testId="stat-admins" />
        <StatCard
          label="Alpha · beta testers"
          value={`${alphaCount} · ${betaCount}`}
          icon={FlaskConical}
          tone="accent"
          onClick={() => setCohort(cohort === 'all' ? 'alpha' : 'all')}
          testId="stat-testers"
        />
      </StatGrid>

      <Card className="aurora-card">
        <CardHeader className="p-5 sm:p-6">
          <CardTitle className="font-heading text-base font-semibold text-foreground">All accounts</CardTitle>
          <CardDescription className="text-pretty">
            Slots / devices are the account's counts. "Active" gates the worker API for the account, not login.
            {!superAdmin && ' Admin accounts are view-only for you (legacy lets only the owner edit them).'}
          </CardDescription>
        </CardHeader>
        <CardContent className="p-5 pt-0 sm:p-6 sm:pt-0">
          {isLoading ? (
            <UsersSkeleton />
          ) : users.length === 0 ? (
            <div className="aurora-card aurora-card-dashed p-8 text-center sm:p-10" data-testid="users-empty">
              <span className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary ring-1 ring-primary/20">
                <Users className="h-6 w-6" />
              </span>
              <p className="mx-auto mt-4 max-w-sm text-sm text-muted-foreground text-pretty">
                {all.length === 0 ? 'No accounts on this server'
                  : cohort === 'alpha' ? 'No alpha testers yet - open an account, Edit → Switches → Alpha tester'
                  : cohort === 'beta' ? 'No beta testers yet - open an account, Edit → Switches → Beta tester'
                  : 'No accounts match the search'}
              </p>
              {all.length === 0 && (
                <Button onClick={() => setAdding(true)} className="tap mt-5 rounded-full">
                  <UserPlus className="h-4 w-4" /> Add user
                </Button>
              )}
            </div>
          ) : (
            <TableScroll
              mobile={users.map((u) => <UserCard key={u.id} user={u} superAdmin={superAdmin} onEdit={setEditing} />)}
            >
              <Table>
                <TableHeader>
                  <TableRow className="border-border hover:bg-transparent">
                    <TableHead className={LABEL}>Account</TableHead>
                    <TableHead className={LABEL}>Role</TableHead>
                    <TableHead className={LABEL}>Automation</TableHead>
                    <TableHead className={LABEL}>Slots / devices</TableHead>
                    <TableHead className={LABEL}>Cohort</TableHead>
                    <TableHead className={LABEL}>Plan</TableHead>
                    <TableHead className={LABEL}>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((u) => <UserRow key={u.id} user={u} superAdmin={superAdmin} onEdit={setEditing} />)}
                </TableBody>
              </Table>
            </TableScroll>
          )}
        </CardContent>
      </Card>

      <AddUserDialog open={adding} onOpenChange={setAdding} superAdmin={superAdmin} roles={roles} onCreated={onChanged} />
      {editing && (
        <EditUserDialog
          user={editing}
          open={!!editing}
          onOpenChange={(o) => { if (!o) setEditing(null); }}
          superAdmin={superAdmin}
          roles={roles}
          onChanged={onChanged}
        />
      )}
    </div>
  );
}
